import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, X, User, LogOut } from "lucide-react";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";

// Fallback navigation items — used ONLY if the server API
// /api/mobile-navigation/client returns empty. Must match desktop
// sidebar exactly (client/src/components/layout/sidebar.tsx
// getClientNavigation function) so desktop ↔ mobile parity is preserved
// even during DB outages. The first 11 items are the main section;
// items 12+ are the Wedding Planning section — the section header is
// rendered before the first item whose href matches WEDDING_PLANNING_START.
const fallbackNavItems = [
  // Main section
  { href: "/", label: "Dashboard", icon: "Home" },
  { href: "/bookings", label: "Bookings", icon: "Calendar" },
  { href: "/invoices", label: "Invoices", icon: "CreditCard" },
  { href: "/event-form", label: "Event Form (New)", icon: "Palette" },
  { href: "/story-weaver", label: "Story Weaver", icon: "Film" },
  { href: "/mc-scheduling", label: "MC Scheduling", icon: "Mic" },
  { href: "/live-stream", label: "Live Stream", icon: "Radio" },
  { href: "/client/addons", label: "Add-ons & Services", icon: "ShoppingBag" },
  { href: "/loyalty-program", label: "Loyalty Program", icon: "Gift" },
  { href: "/family-hub", label: "Family Hub", icon: "Users" },
  { href: "/client-transport", label: "Transport", icon: "Car" },
  // Wedding Planning section
  { href: "/website-v2", label: "Wedding Website", icon: "Globe" },
  { href: "/budget", label: "Budget Calculator", icon: "Calculator" },
  { href: "/invitations", label: "Wedding Invitations", icon: "Mail" },
  { href: "/vendors", label: "Vendor Directory", icon: "Store" },
  { href: "/checklist", label: "Wedding Checklist", icon: "CheckSquare" },
  { href: "/ai-timeline", label: "Timeline & Tasks", icon: "Calendar" },
];

// Href of the first Wedding Planning item — used to insert the section
// header dynamically regardless of what the API returns, so we don't
// couple the UI to a fixed array index.
const WEDDING_PLANNING_START = "/website-v2";

const fallbackAdminNavItems = [
  { href: "/admin/dashboard", label: "Admin Dashboard", icon: "LayoutDashboard" },
  { href: "/admin/clients", label: "Clients", icon: "Users" },
  { href: "/admin/weddings", label: "Weddings", icon: "Heart" },
  { href: "/admin/vendors", label: "Vendors", icon: "Store" },
  { href: "/admin/addons", label: "Add-on Management", icon: "ShoppingBag" },
  { href: "/admin/mc-calendar", label: "MC Calendar", icon: "CalendarDays" },
  { href: "/event-payments", label: "Event Day Payments", icon: "CreditCard" },
  { href: "/admin/analytics", label: "Analytics", icon: "TrendingUp" },
  { href: "/admin/settings", label: "Settings", icon: "Settings" },
];

const fallbackEmployeeNavItems = [
  { href: "/", label: "Dashboard", icon: "Home" },
  { href: "/employee/schedule", label: "My Calendar", icon: "Calendar" },
];

export default function HamburgerNav() {
  const [isOpen, setIsOpen] = useState(false);
  const [location] = useLocation();
  const { user, logout } = useAuth();

  const { data: navigationItems = [] } = useQuery({
    queryKey: [`/api/mobile-navigation/${user?.role || 'client'}`],
    enabled: !!user?.role,
    staleTime: 0, // Always fetch fresh data
    cacheTime: 0, // Don't cache
  });

  // Use database items if available, otherwise fallback to role-specific defaults
  const getNavItems = () => {
    // For clients, always use the API data if available, otherwise fallback
    if (user?.role === 'client') {
      return navigationItems.length > 0 ? navigationItems : fallbackNavItems;
    }
    // For employees, use employee-specific fallback
    if (user?.role === 'employee') {
      return navigationItems.length > 0 ? navigationItems : fallbackEmployeeNavItems;
    }
    // For admin/event managers, use their specific fallbacks
    if (navigationItems.length > 0) return navigationItems;
    return (user?.role === 'admin' || user?.role === 'event_manager') ? fallbackAdminNavItems : fallbackNavItems;
  };

  const navItems = getNavItems();

  // Debug: Log menu items count
  if (user?.role === 'client') {
    console.log('[HamburgerNav] Client menu items count:', navItems.length);
    console.log('[HamburgerNav] Menu items:', navItems.map(i => i.label));
  }

  const getIcon = (iconName: string) => {
    const IconComponent = Icons[iconName as keyof typeof Icons] as React.ComponentType<any>;
    return IconComponent || Icons.Home;
  };

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  return (
    <div className="lg:hidden">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="mobile-nav-optimize p-2 text-white hover:text-[#B8860B] hover:bg-gray-800"
          >
            <Menu className="w-6 h-6" />
            <span className="sr-only">Open navigation menu</span>
          </Button>
        </SheetTrigger>
        
        <SheetContent 
          side="left" 
          className="w-72 bg-gray-900 border-[#B8860B]/20 mobile-modal-optimize mobile-menu-optimize !bg-gray-900"
          style={{ backgroundColor: '#1f2937', borderColor: 'rgba(184, 134, 11, 0.2)' }}
        >
          <SheetHeader className="mobile-touch-spacing">
            <SheetTitle className="text-white text-xl font-bold" style={{ color: 'white !important' }}>
              LA Mirage
            </SheetTitle>
            
            {user && (
              <div className="flex items-center space-x-3 pt-4 pb-6 border-b border-[#B8860B]/20">
                <div className="w-10 h-10 bg-[#B8860B] rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-black" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium truncate">
                    {user.firstName || user.username}
                  </p>
                  <p className="text-gray-400 text-sm capitalize">
                    {user.role} Account
                  </p>
                </div>
              </div>
            )}
          </SheetHeader>

          <div className="flex flex-col py-6 space-y-2 mobile-card-optimize">
            {navItems.map((item) => {
              const Icon = getIcon(item.icon);
              const isActive = location === item.href;

              // Add "Wedding Planning" section header before the first
              // planning-section item. Href-based check is drift-proof —
              // works no matter where the API puts the item in the list.
              const showWeddingPlanningHeader =
                item.href === WEDDING_PLANNING_START && user?.role === 'client';

              return (
                <div key={item.href}>
                  {showWeddingPlanningHeader && (
                    <div className="text-xs font-bold text-amber-300/80 uppercase tracking-wider px-4 py-2 mt-3 mb-1 bg-gradient-to-r from-[#B8860B]/10 to-transparent rounded-lg border-l-2 border-[#B8860B]/40">
                      Wedding Planning
                    </div>
                  )}
                  <Link
                    href={item.href}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors mobile-touch-spacing",
                      isActive
                        ? "text-[#B8860B] bg-[#B8860B]/10 border-l-4 border-[#B8860B]"
                        : "text-gray-300 hover:text-[#B8860B] hover:bg-gray-800/50"
                    )}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="border-t border-[#B8860B]/20 pt-4 mt-auto">
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10 mobile-touch-spacing"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}